import {
    type QueryClient,
    queryOptions,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import {
    RECENTLY_VIEWED_LIMIT,
    type RecordListingViewInput,
} from "./saved-listing.contract";
import { getRecentlyViewedListingIds, recordListingViewed } from "./commands";

const RECENTLY_VIEWED_STALE_TIME_MS = 5 * 60_000;

const RECENTLY_VIEWED_QUERY_KEY: readonly ["recentlyViewedListingIds"] = [
    "recentlyViewedListingIds",
];

const recentlyViewedListingIdsQueryOptions = queryOptions({
    queryKey: RECENTLY_VIEWED_QUERY_KEY,
    queryFn: () => getRecentlyViewedListingIds(),
    staleTime: RECENTLY_VIEWED_STALE_TIME_MS,
    refetchOnWindowFocus: false,
});

export function prefetchRecentlyViewedListingIds(queryClient: QueryClient) {
    return queryClient.ensureQueryData(recentlyViewedListingIdsQueryOptions);
}

export function useRecordListingView() {
    const queryClient = useQueryClient();
    return async (data: RecordListingViewInput) => {
        await recordListingViewed({ data });

        queryClient.setQueryData<Array<{ listingId: string; viewedAt: Date }>>(
            RECENTLY_VIEWED_QUERY_KEY,
            (prev) =>
                prev
                    ? [
                          { listingId: data.listingId, viewedAt: new Date() },
                          ...prev.filter((i) => i.listingId !== data.listingId),
                      ].slice(0, RECENTLY_VIEWED_LIMIT)
                    : prev,
        );
    };
}

export function useRecentlyViewedListingIds() {
    return useQuery({
        ...recentlyViewedListingIdsQueryOptions,
        notifyOnChangeProps: ["data", "isLoading", "isError"],
    });
}
